/**
 * Data Transformation Utilities
 * Framework-agnostic data transformation and mapping functions
 */

/**
 * Extract data from various API response formats
 * Handles different backend response structures
 */
export function extractApiData<T>(response: any, ...paths: string[]): T[] {
  // Handle null/undefined
  if (!response) {
    return [];
  }

  // Handle direct array
  if (Array.isArray(response)) {
    return response;
  }

  // Try each path in order
  for (const path of paths) {
    const keys = path.split(".");
    let current = response;

    for (const key of keys) {
      if (current && current.hasOwnProperty(key)) {
        current = current[key];
      } else {
        current = null;
        break;
      }
    }

    if (current && Array.isArray(current)) {
      return current;
    }
  }

  // Fallback: return empty array
  console.warn("Could not extract array from API response:", response);
  return [];
}

/**
 * Safe API data extractor with common patterns
 */
export function extractDataSafe<T>(response: any): T[] {
  return extractApiData<T>(
    response,
    "data.region",
    "data.state",
    "data.city",
    "data.brand",
    "data.brandCategory",
    "data.language",
    "data.unit",
    "data.banner",
    "data.credentials",
    "data",
    "result",
    "items",
  );
}

/**
 * Transform object keys (camelCase <-> snake_case)
 */
export function transformKeys(
  obj: any,
  transformer: (key: string) => string,
): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item, transformer));
  }

  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  const result: any = {};
  Object.keys(obj).forEach((key) => {
    const newKey = transformer(key);
    result[newKey] = transformKeys(obj[key], transformer);
  });

  return result;
}

/**
 * Convert snake_case to camelCase
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert camelCase to snake_case
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepClone(item)) as any;
  }

  const cloned: any = {};
  Object.keys(obj).forEach((key) => {
    cloned[key] = deepClone((obj as any)[key]);
  });

  return cloned;
}

/**
 * Pick specific properties from an object
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Pick<T, K> {
  const result: any = {};
  keys.forEach((key) => {
    if (obj.hasOwnProperty(key)) {
      result[key] = obj[key];
    }
  });
  return result;
}

/**
 * Omit specific properties from an object
 */
export function omit<T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Omit<T, K> {
  const result: any = { ...obj };
  keys.forEach((key) => {
    delete result[key];
  });
  return result;
}

/**
 * Group array of objects by a key
 */
export function groupBy<T>(
  array: T[],
  keyGetter: (item: T) => string | number,
): Record<string, T[]> {
  const result: Record<string, T[]> = {};

  array.forEach((item) => {
    const key = String(keyGetter(item));
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
  });

  return result;
}

/**
 * Create a lookup map from array
 */
export function arrayToMap<T>(
  array: T[],
  keyGetter: (item: T) => string | number,
): Map<string | number, T> {
  const map = new Map<string | number, T>();
  array.forEach((item) => {
    const key = keyGetter(item);
    map.set(key, item);
  });
  return map;
}

/**
 * Flatten nested array
 */
export function flatten<T>(array: any[]): T[] {
  return array.reduce((flat, item) => {
    return flat.concat(Array.isArray(item) ? flatten(item) : item);
  }, []);
}

/**
 * Remove falsy values from array
 */
export function compact<T>(
  array: (T | null | undefined | false | "" | 0)[],
): T[] {
  return array.filter(Boolean) as T[];
}

/**
 * Get unique values from array
 */
export function unique<T>(array: T[]): T[] {
  return [...new Set(array)];
}

/**
 * Get unique objects by key
 */
export function uniqueBy<T>(
  array: T[],
  keyGetter: (item: T) => string | number,
): T[] {
  const seen = new Set<string | number>();
  return array.filter((item) => {
    const key = keyGetter(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * Sort array of objects by key
 */
export function sortBy<T>(
  array: T[],
  keyGetter: (item: T) => any,
  order: "asc" | "desc" = "asc",
): T[] {
  return [...array].sort((a, b) => {
    const aVal = keyGetter(a);
    const bVal = keyGetter(b);

    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });
}

/**
 * Chunk array into smaller arrays
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Check if two objects are deeply equal
 */
export function isEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null) return false;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!isEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/**
 * Merge objects deeply
 */
export function deepMerge<T extends object>(
  target: T,
  ...sources: Partial<T>[]
): T {
  if (!sources.length) return target;

  const source = sources.shift();
  if (!source) return deepMerge(target, ...sources);

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge((target as any)[key], source[key] as any);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepMerge(target, ...sources);
}

function isObject(item: any): boolean {
  return item && typeof item === "object" && !Array.isArray(item);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return function (...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
